import { type JSX, splitProps } from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv, type VariantProps } from '../lib/tv.ts'

/**
 * Fields are rectangular with a soft radius, never pills — shape is how you tell a thing you
 * press from a thing you type into.
 *
 * `mono` exists because a part number, an order code and a quantity are read by glyph shape
 * rather than as words. Proportional digits defeat that, and so does typing `1O0` into a
 * proportional face and not seeing it.
 */
const input = tv({
  base: [
    'h-(--size-control-md) w-full rounded-control px-control-x-sm',
    'border border-border bg-surface text-body text-text',
    'outline-none transition-[border-color,background-color] duration-(--duration-fast) ease-out',
    'placeholder:text-text-subtle',
    'hover:border-border-strong',
    'focus:border-accent focus:bg-surface focus:ring-0',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    'aria-[invalid=true]:border-status-critical aria-[invalid=true]:focus-visible:outline-status-critical',
    'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-subtle',
  ],
  variants: {
    font: {
      sans: '',
      mono: 'font-mono tracking-tight tabular-nums',
    },
  },
  defaultVariants: { font: 'sans' },
})

export type InputProps = JSX.InputHTMLAttributes<HTMLInputElement> & VariantProps<typeof input>

export const Input = (props: InputProps) => {
  const [local, rest] = splitProps(props, ['font', 'class'])

  return <input {...rest} class={cn(input({ font: local.font }), local.class)} />
}
